import * as React from "react";
import {
  APP_AWAY_EVENT,
  subscribeAppResume,
} from "../lib/appResumeCoordinator";
import { createSingleFlightRefresh } from "../lib/singleFlightRefresh";

export interface CoordinatedRefreshOptions {
  enabled?: boolean;
  pollMs?: number;
  requestOnMount?: boolean;
  refreshOnResume?: boolean;
}

/**
 * One mounted refresh domain: no hidden polling, coalesced resume catch-up,
 * and at most one trailing request while a prior request is in flight.
 */
export function useCoordinatedRefresh(
  load: () => Promise<void>,
  options: CoordinatedRefreshOptions = {}
): () => void {
  const {
    enabled = true,
    pollMs,
    requestOnMount = true,
    refreshOnResume = true,
  } = options;
  const loadRef = React.useRef(load);
  loadRef.current = load;
  const refreshRef = React.useRef<ReturnType<typeof createSingleFlightRefresh> | null>(null);
  if (refreshRef.current === null) {
    refreshRef.current = createSingleFlightRefresh(
      () => loadRef.current(),
      () => document.visibilityState === "visible"
    );
  }
  const requestRefresh = React.useCallback(() => {
    if (enabled) refreshRef.current?.request();
  }, [enabled]);

  React.useEffect(() => {
    if (enabled && requestOnMount) requestRefresh();
  }, [enabled, requestOnMount, requestRefresh]);

  React.useEffect(() => {
    if (!enabled) {
      refreshRef.current?.cancelPending();
      return;
    }

    let timer: number | null = null;
    const stopTimer = () => {
      if (timer !== null) window.clearInterval(timer);
      timer = null;
    };
    const startTimer = () => {
      stopTimer();
      if (pollMs && document.visibilityState === "visible") {
        timer = window.setInterval(requestRefresh, pollMs);
      }
    };
    const onAway = () => stopTimer();
    const unsubscribeResume = subscribeAppResume(() => {
      if (refreshOnResume) requestRefresh();
      startTimer();
    });
    window.addEventListener(APP_AWAY_EVENT, onAway);
    startTimer();
    return () => {
      stopTimer();
      unsubscribeResume();
      window.removeEventListener(APP_AWAY_EVENT, onAway);
      refreshRef.current?.cancelPending();
    };
  }, [enabled, pollMs, refreshOnResume, requestRefresh]);

  return requestRefresh;
}
